// First value checklist (contract 2.4): a details block in the context list,
// never an overlay, never blocking. Steps are derived client side (api-surface
// 2.1: no checklist endpoint): connectors, archive coverage, alert rules, and
// two flags the views persist under preferences.firstValue. Collapse state is
// remembered in localStorage; the block never hides itself.
import { useView } from '../router/useView';
import { useShell } from './ShellContext';

export interface FirstValueFlags {
  readonly researchedCoin?: boolean;
  readonly savedWatchlist?: boolean;
}

export interface FirstValueInputs {
  readonly connectedSources: number;
  readonly matchingRecords: number | null;
  readonly alertRules: number | null;
  readonly flags: FirstValueFlags | null;
}

export interface FirstValueStep {
  readonly id: string;
  readonly label: string;
  readonly done: boolean;
  readonly view: 'sources' | 'live' | 'checker' | 'watchlists' | 'alerts';
}

export function deriveFirstValueSteps(input: FirstValueInputs): ReadonlyArray<FirstValueStep> {
  return [
    { id: 'source', label: 'Connect a source', done: input.connectedSources > 0, view: 'sources' },
    { id: 'calls', label: 'See calls in Live Calls', done: (input.matchingRecords ?? 0) > 0, view: 'live' },
    { id: 'research', label: 'Research a coin', done: input.flags?.researchedCoin === true, view: 'checker' },
    { id: 'watchlist', label: 'Save a watchlist', done: input.flags?.savedWatchlist === true, view: 'watchlists' },
    { id: 'alert', label: 'Set an alert', done: (input.alertRules ?? 0) > 0, view: 'alerts' },
  ];
}

export function FirstValueChecklist({ input }: { readonly input: FirstValueInputs }) {
  const { prefs, toggleChecklist } = useShell();
  const { navigate } = useView();
  const steps = deriveFirstValueSteps(input);
  const done = steps.filter((step) => step.done).length;
  return (
    <details className="checklist" open={prefs.checklist === 'open'} onToggle={(event) => { if (event.currentTarget.open !== (prefs.checklist === 'open')) toggleChecklist(); }}>
      <summary>
        <span>First value</span>
        <span className="checklist-n num">{done} of {steps.length}</span>
      </summary>
      <ol>
        {steps.map((step) => (
          <li key={step.id} data-done={step.done}>
            <span className="checklist-box" aria-hidden="true" />
            <button type="button" className="checklist-step" onClick={() => navigate(step.view)}>{step.label}</button>
          </li>
        ))}
      </ol>
    </details>
  );
}
